// FOLDING A PLAIN-TEXT EMAIL — with or without its line breaks.
//
// Track stored bodies with every blank line collapsed until 27/08 (see
// functions/body-text.js), so a message filed before then is ONE RUN of text.
// Every boundary the reader draws — where the quoted history starts, where the
// signature starts, where the sender's list of questions is — was keyed off a
// newline, so on a real stored email none of them fired and the whole mail
// rendered as a wall (Karan 28/08, A0297).
//
// These matchers work either way. They are anchored on words that only appear
// at a real boundary — "From: … Sent:", a sign-off followed by a job title or a
// phone number, a run of asterisk bullets — so they do not fire mid-sentence.

// Where the quoted history begins.
const QUOTE_AT = [
  /-{2,}\s*Forwarded message\s*-{2,}/i,
  /\bBegin forwarded message:/i,
  /\bFrom:\s*[^\n]{0,120}?\bSent:/i,          // Outlook, inline or on its own line
  /\n\s*(>\s*)?From:\s/,                       // quoted header block
  /\bOn\b[^\n]{5,160}?\bwrote:/i,
];

// Where the sender's own signature begins, once they have signed off.
const SIGNOFF = /\b(Kind regards|Best regards|Warm regards|Many thanks|Best wishes|Kind Regards|Regards|Yours sincerely|Yours faithfully)\b/i;
const SIG_START = /(\b(?:Associate|Director|Partner|Manager|Consultant|Engineer|Architect|Surveyor|Solicitor|Principal|Senior|Head of)\b|\b[TMF]\s*\+?\d|\bTel\b|\bMob(?:ile)?\b|www\.|https?:\/\/)/i;

// The legal block a firm appends — folded separately, inside the quote.
const LEGAL_AT = [
  /General Data Protection Regulations?\b/i,
  /\bCybercrime and fraud\b/i,
  /Solicitors Regulation Authority/i,
  /IMPORTANT:\s*The information/i,
  /This (e-?mail|message)( and any attachment)?s?\b[^.]{0,60}(confidential|intended|virus)/i,
];

const earliest = (text, pats, min = 40) => {
  let at = -1;
  for (const p of pats) {
    const m = p.exec(text);
    if (m && m.index > min && (at < 0 || m.index < at)) at = m.index;
  }
  return at;
};

/** Where the quoted history starts in `t`, or -1. */
export const quoteCut = (t) => earliest(String(t || ''), QUOTE_AT);

/** Where a legal/boilerplate footer starts in `t`, or -1. */
export const legalCut = (t) => earliest(String(t || ''), LEGAL_AT, 0);

/**
 * Where the sender's signature starts — AFTER their sign-off, at the first job
 * title, phone number or web address. The sign-off and the name stay with the
 * message, which is what artboard 2a shows ("Kind regards / Sharon Tetteh",
 * then "Show signature").
 */
export function signatureCut(t) {
  const s = String(t || '');
  const so = SIGNOFF.exec(s);
  if (!so) return -1;
  const m = SIG_START.exec(s.slice(so.index));
  return m ? so.index + m.index : -1;
}

// A run of asterisk / dash / numbered points, whether or not it kept its lines.
const BULLET_SPLIT = /(?:^|\s)[*•·]\s+|\n[ \t]*(?:\(?[a-z]\)|\(?\d{1,2}[.)])\s+/g;

/**
 * Pull a sender's list of questions out of the message.
 * Returns { items, before, after } — `after` is the prose that followed the last
 * point, which in a flat body runs straight on from it.
 */
export function splitPoints(message) {
  const s = String(message || '');
  // A point's own line breaks are just wrapping — collapse them, or it renders
  // broken mid-phrase.
  const parts = s.split(BULLET_SPLIT).map((x) => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
  if (parts.length < 3) return { items: [], before: s, after: '' };   // head + 2 points minimum
  const before = parts[0];
  const items = parts.slice(1);
  // The final point has no delimiter after it, so it swallows whatever prose
  // followed. Cut it at its own closing "?" or ";".
  // The last point has no delimiter after it, so in a flat body it swallows the
  // prose that followed. Cut it at its own end: a "?" or ";" if it has one, and
  // otherwise a full stop that starts a new sentence — a point can end "…
  // evidence obtained." just as easily as "… reinforced?".
  const last = items[items.length - 1];
  const end = /[?;]|\.(?=\s+[A-Z])/.exec(last);
  let after = '';
  if (end) {
    after = last.slice(end.index + 1).trim();
    items[items.length - 1] = last.slice(0, end.index + 1).trim();
  }
  return { items, before, after };
}

// The quoted header block itself — "From: … Sent: … To: … Subject: …". The box
// already names who wrote it and when, so repeating the block inside it is
// noise; artboard 2a starts the quote at "Dear Sirs,".
const HEADER_RUN = /^\s*(?:>\s*)?From:[\s\S]{0,400}?\bSubject:[^\n]{0,300}?(?=\s(?:Dear|Hi|Hello|Good\s+(?:morning|afternoon|evening))\b|\n)/i;

/** Drop a quoted header block from the front of `t`. */
export function stripQuoteHeader(t) {
  return String(t || '').replace(HEADER_RUN, '').replace(/^\s*[\n>]+/, '').trim();
}

/** When the quoted message was sent, from its own "Sent:" line. */
export function quotedSentAt(t) {
  const m = /\bSent:\s*(?:[A-Za-z]+,\s*)?(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/i.exec(String(t || ''));
  if (!m) return null;
  const months = ['january','february','march','april','may','june','july','august','september','october','november','december'];
  const mi = months.findIndex((x) => x.startsWith(m[2].toLowerCase().slice(0, 3)));
  if (mi < 0) return null;
  return new Date(+m[3], mi, +m[1], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0);
}
